import { Controller } from '@hotwired/stimulus'
import {
  googleMapsEmbedUrl,
  googleMapsOpenUrl,
  needsGoogleMapsLinkResolve,
  parseGoogleMapsLink,
} from '../lib/google_maps_link'

const NUDGE = 0.00015
const DEFAULT_COUNTRY = 'Pakistan'

export default class extends Controller {
  static targets = [
    'latInput',
    'lngInput',
    'status',
    'mapPreview',
    'mapPreviewWrap',
    'linkInput',
    'openMapsLink',
    'nudgePanel',
    'pickerPanel',
    'addressInput',
    'cityInput',
    'postalCodeInput',
    'countryInput',
  ]

  static values = {
    lat: Number,
    lng: Number,
    resolveLinkUrl: String,
    reverseUrl: String,
  }

  connect () {
    this.ensureDefaultCountry()
    this.syncFromInputs()
  }

  disconnect () {
    if (this.reverseTimer) window.clearTimeout(this.reverseTimer)
  }

  openPicker (event) {
    event.preventDefault()
    if (!this.hasPickerPanelTarget) return

    this.pickerPanelTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
  }

  closePicker (event) {
    event.preventDefault()
    if (!this.hasPickerPanelTarget) return

    this.pickerPanelTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
  }

  confirmPicker (event) {
    event.preventDefault()
    this.setStatus('Location confirmed. Save settings to publish it.')
    this.closePicker(event)
  }

  useCurrentLocation (event) {
    event.preventDefault()

    if (!navigator.geolocation) {
      this.setStatus('Your browser does not support location. Paste a Google Maps link instead.')
      return
    }

    this.setStatus('Getting your location…')

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.applyCoords(position.coords.latitude, position.coords.longitude)
        this.setStatus('Location captured. Address fields updated from the map pin - check and save.')
      },
      (error) => {
        const message = {
          1: 'Location permission denied. Allow location access or paste a Google Maps link.',
          2: 'Location unavailable. Paste a Google Maps link from your phone.',
          3: 'Location timed out. Try again or paste a Google Maps link.',
        }[error.code] || 'Could not read your location. Paste a Google Maps link instead.'
        this.setStatus(message)
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    )
  }

  async applyLink (event) {
    event.preventDefault()
    if (!this.hasLinkInputTarget) return

    const raw = this.linkInputTarget.value.trim()
    if (!raw) {
      this.setStatus('Paste a Google Maps share link first.')
      return
    }

    let coords = parseGoogleMapsLink(raw)

    if (!coords && needsGoogleMapsLinkResolve(raw)) {
      coords = await this.resolveLinkOnServer(raw)
    }

    if (!coords) {
      this.setStatus('Could not read that link. Share from Google Maps app, or paste coordinates like 31.5204, 74.3587.')
      return
    }

    this.applyCoords(coords.lat, coords.lng)
    this.setStatus('Location loaded from link. Address & city updated - review then save.')
  }

  linkInputChanged () {
    if (!this.hasLinkInputTarget) return
    const coords = parseGoogleMapsLink(this.linkInputTarget.value)
    if (coords) this.applyCoords(coords.lat, coords.lng, { quiet: true })
  }

  async resolveLinkOnServer (url) {
    if (!this.hasResolveLinkUrlValue) return null

    this.setStatus('Reading Google Maps link…')

    try {
      const response = await fetch(`${this.resolveLinkUrlValue}?${new URLSearchParams({ url })}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      const data = await response.json()
      if (!data.ok) {
        this.setStatus(data.error || 'Could not read that link.')
        return null
      }
      return { lat: data.lat, lng: data.lng }
    } catch (_error) {
      this.setStatus('Link lookup failed. Try again or use "I\'m at my shop now".')
      return null
    }
  }

  nudge (event) {
    event.preventDefault()
    const saved = this.readCoords()
    if (!saved) {
      this.setStatus('Set a location first.')
      return
    }

    const direction = event.currentTarget.dataset.nudgeDirection
    let { lat, lng } = saved

    switch (direction) {
      case 'north': lat += NUDGE; break
      case 'south': lat -= NUDGE; break
      case 'east': lng += NUDGE; break
      case 'west': lng -= NUDGE; break
      default: break
    }

    this.applyCoords(lat, lng, { quiet: true })
    this.setStatus('Pin nudged. Address will refresh - save when the map looks correct.')
  }

  clearPin (event) {
    event.preventDefault()

    if (this.reverseTimer) window.clearTimeout(this.reverseTimer)
    this.latInputTarget.value = ''
    this.lngInputTarget.value = ''
    if (this.hasLinkInputTarget) this.linkInputTarget.value = ''
    this.updatePreview(null, null)
    this.setStatus('Location cleared.')
  }

  applyCoords (lat, lng, { quiet = false } = {}) {
    const safeLat = Number(lat)
    const safeLng = Number(lng)
    if (!Number.isFinite(safeLat) || !Number.isFinite(safeLng)) return

    this.latInputTarget.value = safeLat.toFixed(7)
    this.lngInputTarget.value = safeLng.toFixed(7)
    this.updatePreview(safeLat, safeLng)
    this.scheduleReverseGeocode(safeLat, safeLng)
    if (!quiet) this.setStatus('Location updated. Filling address from map pin…')
  }

  scheduleReverseGeocode (lat, lng) {
    if (this.reverseTimer) window.clearTimeout(this.reverseTimer)
    this.reverseTimer = window.setTimeout(() => {
      this.fillAddressFromCoords(lat, lng)
    }, 550)
  }

  async fillAddressFromCoords (lat, lng) {
    if (!this.hasReverseUrlValue) {
      this.ensureDefaultCountry()
      return
    }

    try {
      const response = await fetch(`${this.reverseUrlValue}?${new URLSearchParams({ lat, lng })}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      const data = await response.json()
      if (!data.ok) {
        this.ensureDefaultCountry()
        return
      }

      if (this.hasAddressInputTarget && data.address_line1) {
        this.addressInputTarget.value = data.address_line1
      }
      if (this.hasCityInputTarget && data.city) {
        this.cityInputTarget.value = data.city
      }
      if (this.hasPostalCodeInputTarget && data.postal_code) {
        this.postalCodeInputTarget.value = data.postal_code
      }
      if (this.hasCountryInputTarget) {
        this.countryInputTarget.value = data.country || DEFAULT_COUNTRY
      }

      this.setStatus('Address, city & country updated from the pin. Edit if needed, then save.')
    } catch (_error) {
      this.ensureDefaultCountry()
      this.setStatus('Pin set, but address lookup failed. Fill address manually, then save.')
    }
  }

  ensureDefaultCountry () {
    if (!this.hasCountryInputTarget) return
    if (!this.countryInputTarget.value.trim()) {
      this.countryInputTarget.value = DEFAULT_COUNTRY
    }
  }

  syncFromInputs () {
    const saved = this.readCoords()
    if (!saved) {
      this.updatePreview(null, null)
      return
    }

    this.latInputTarget.value = saved.lat.toFixed(7)
    this.lngInputTarget.value = saved.lng.toFixed(7)
    this.updatePreview(saved.lat, saved.lng)
  }

  updatePreview (lat, lng) {
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng)

    if (this.hasMapPreviewWrapTarget) {
      this.mapPreviewWrapTarget.classList.toggle('hidden', !hasCoords)
    }
    if (this.hasNudgePanelTarget) {
      this.nudgePanelTarget.classList.toggle('hidden', !hasCoords)
    }

    if (!this.hasMapPreviewTarget) return

    if (!hasCoords) {
      this.mapPreviewTarget.removeAttribute('src')
      return
    }

    this.mapPreviewTarget.src = googleMapsEmbedUrl(lat, lng)

    if (this.hasOpenMapsLinkTarget) {
      this.openMapsLinkTarget.href = googleMapsOpenUrl(lat, lng)
    }
  }

  readCoords () {
    const lat = parseFloat(this.latInputTarget?.value)
    const lng = parseFloat(this.lngInputTarget?.value)
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng }
    }
    if (this.hasLatValue && this.hasLngValue) {
      return { lat: this.latValue, lng: this.lngValue }
    }
    return null
  }

  setStatus (message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
    }
  }
}
